import React from 'react';
import { Alert, Avatar } from 'antd';
import {
  AgentFooter,
  AgentText,
  UserText,
  ErrorEvent,
  StyledCollapse,
  EventRecordWrapper,
} from './styled';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  NexaEvent,
  showReasoningAtom,
  showActionButtonsAtom,
  suggestedActionsAtom,
  actionBubblesLoadingAtom,
} from '../NexaStore';
import DashboardLink, { MergedDashboardLink } from '../elements/DashboardLink';
import { FlexFill } from '../../common/Utils/Layout';
import { UserOutlined } from '@ant-design/icons';
import Feedback from '../elements/Feedback';
import { RetryButton } from '../elements/RetryButton';
import ActionBubbleBar from '../ActionBubbles';
import ActionBubbleShimmer from '../ActionBubbles/ActionBubbleShimmer';

import { AUTHOR_NEXA_AGENT, AUTHOR_USER } from '../utils';
import { FALLBACK_ERROR_MESSAGE, FunctionCall } from './utils';
import { CopyBtn } from '../elements/CopyBtn';
import MarkTable from '../elements/Visualization/MarkTable';
import { sanitizeMarkdown } from './mdUtils';
import ErrorBoundary from '@src/components/common/ErrorBoundary';
import { useAtomValue } from 'jotai';
import useMergedDashboardLinks from '../hooks/useMergedDashboardLinks';

const EventRecord = ({
  id = '-',
  author,
  type = '',
  value = null,
  thought = false,
  functionCalls = [],
  isLastAgentInSequence = false,
  isLastAgentEventOverall = false,
  hasNonThoughtAfter = false,
  precedingUserQuery,
}: NexaEvent & {
  functionCalls?: FunctionCall[];
  isLastAgentInSequence?: boolean;
  isLastAgentEventOverall?: boolean;
  hasNonThoughtAfter?: boolean;
  precedingUserQuery?: string;
}) => {
  const showReasoning = useAtomValue(showReasoningAtom);
  const showActionButtons = useAtomValue(showActionButtonsAtom);
  const suggestedActions = useAtomValue(suggestedActionsAtom);
  const actionBubblesLoading = useAtomValue(actionBubblesLoadingAtom);

  // Get merged and standalone function calls using the hook
  const { merged: mergedFunctionCalls, standalone: standaloneFunctionCalls } =
    useMergedDashboardLinks(functionCalls as any);

  // Control collapse state: open by default, close when non-thought text arrives
  const [thoughtCollapseOpen, setThoughtCollapseOpen] = React.useState<string[]>(() => {
    // Open by default for thought blocks (unless there's already a non-thought after it)
    return thought && showReasoning && !hasNonThoughtAfter ? [id || '1'] : [];
  });

  // Auto-close thought when a non-thought text arrives after it
  React.useEffect(() => {
    if (thought && showReasoning && hasNonThoughtAfter) {
      // A non-thought text block has arrived after this thought - close it
      setThoughtCollapseOpen([]);
    }
  }, [hasNonThoughtAfter, thought, showReasoning]);

  // Extract common footer rendering logic
  const renderAgentFooter = ({
    showDashboardLinks = true,
    showCopyButton = false,
    showRetryButton = false,
  }) => {
    if (!isLastAgentInSequence) return null;

    // Render merged dashboard links first (combined tool calls)
    const renderedMergedLinks =
      showDashboardLinks && mergedFunctionCalls && mergedFunctionCalls.length > 0
        ? mergedFunctionCalls.map((mergedConfig, idx) => (
            <MergedDashboardLink
              key={`merged-${mergedConfig.sourceIds.join('-')}-${idx}`}
              mergedConfig={mergedConfig}
            />
          ))
        : [];

    // Render standalone (non-merged) function calls
    const renderedFunctionCalls =
      showDashboardLinks && standaloneFunctionCalls && standaloneFunctionCalls.length > 0
        ? standaloneFunctionCalls.map((fnCall) => (
            <React.Fragment key={fnCall.id}>
              <DashboardLink key={fnCall.id} fnCallConfig={fnCall.value} />
            </React.Fragment>
          ))
        : [];

    return (
      <AgentFooter>
        {renderedMergedLinks}
        {renderedFunctionCalls}
        {showCopyButton && value && typeof value === 'string' && <CopyBtn value={value} />}
        {showRetryButton && precedingUserQuery && <RetryButton query={precedingUserQuery} />}
        <FlexFill />
        <Feedback eventId={id} />
      </AgentFooter>
    );
  };

  const renderActionBubbles = () => {
    if (!isLastAgentEventOverall || !showActionButtons) return null;
    if (actionBubblesLoading) return <ActionBubbleShimmer />;
    if (suggestedActions.length > 0) {
      return <ActionBubbleBar actions={suggestedActions.slice(0, 5)} />;
    }
    return null;
  };

  const renderContent = () => {
    if (author === AUTHOR_USER) {
      return (
        <div
          style={{
            marginRight: 0,
            marginBottom: 10,
            display: 'flex',
            gap: '5px',
            alignItems: 'center',
          }}
        >
          <UserText>
            <div>
              <Avatar
                style={{ backgroundColor: 'transparent', color: '#333' }}
                icon={<UserOutlined />}
              />
            </div>
            <div style={{ flex: 1 }}>{value}</div>
          </UserText>
        </div>
      );
    } else if (author.startsWith(AUTHOR_NEXA_AGENT)) {
      if (type === 'text') {
        // Handle empty text fallback and sanitize markdown
        const displayValue =
          !value || (typeof value === 'string' && value.trim() === '')
            ? FALLBACK_ERROR_MESSAGE
            : sanitizeMarkdown(value);

        const textContent = (
          <AgentText style={thought ? { fontSize: '14px' } : {}}>
            <div>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  table: ({ children }) => <MarkTable>{children}</MarkTable>,
                  th: ({ children }) => (
                    <th
                      style={{
                        border: '1px solid #d9d9d9',
                        padding: '8px 12px',
                        backgroundColor: '#fafafa',
                        fontWeight: 'bold',
                        textAlign: 'left',
                      }}
                    >
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td
                      style={{
                        border: '1px solid #d9d9d9',
                        padding: '8px 12px',
                      }}
                    >
                      {children}
                    </td>
                  ),
                }}
              >
                {displayValue}
              </ReactMarkdown>
            </div>
          </AgentText>
        );

        // If this is a thought event, wrap in Collapse
        if (thought) {
          if (showReasoning) {
            const collapseItems = [
              {
                key: id || '1',
                label: 'Model Reasoning',
                children: textContent,
              },
            ];

            return (
              <div style={{ marginLeft: 0, overflowX: 'auto', marginBottom: 10 }}>
                <StyledCollapse
                  ghost
                  items={collapseItems}
                  activeKey={thoughtCollapseOpen}
                  onChange={(keys) => setThoughtCollapseOpen(keys as string[])}
                />
                {renderActionBubbles()}
                {renderAgentFooter({
                  showDashboardLinks: true,
                  showCopyButton: true,
                  showRetryButton: true,
                })}
              </div>
            );
          }
          return <></>;
        }

        // Regular text event (not a thought)
        return (
          <div style={{ marginLeft: 0, overflowX: 'auto', marginBottom: 10 }}>
            {textContent}
            {renderActionBubbles()}
            {renderAgentFooter({
              showDashboardLinks: true,
              showCopyButton: true,
              showRetryButton: true,
            })}
          </div>
        );
      } else if (type === 'error') {
        return (
          <div style={{ marginBottom: 10 }}>
            <ErrorEvent>
              <Alert message={value.message} type="error" showIcon />
              <div style={{ width: '100%' }}></div>
            </ErrorEvent>
            {renderAgentFooter({
              showDashboardLinks: false,
              showCopyButton: false,
              showRetryButton: true,
            })}
          </div>
        );
      } else if (['function_call', 'function_response'].includes(type)) {
        // Function calls are now handled via pre-computed functionCalls prop
        return null;
      }
      return null;
    }
    return null;
  };

  return (
    <ErrorBoundary>
      <EventRecordWrapper data-event-id={id}>{renderContent()}</EventRecordWrapper>
    </ErrorBoundary>
  );
};

export default EventRecord;
